<template>
  <div class="space-y-6">
    <h2 class="text-3xl font-bold text-gray-900">Dashboard</h2>

    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      <p class="mt-2 text-gray-600">Loading dashboard statistics...</p>
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error</h3>
          <div class="mt-2 text-sm text-red-700">{{ error }}</div>
        </div>
      </div>
    </div>

    <div v-else class="space-y-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Top Row: API Keys and Plans -->
        <div class="card">
          <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Total API Keys</h3>
          <div class="mt-2 text-3xl font-bold text-gray-900">{{ stats?.totalApiKeys || 0 }}</div>
        </div>

        <div class="card">
          <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Plans</h3>
          <div class="mt-2 text-3xl font-bold text-gray-900">{{ stats?.totalPlans || 0 }}</div>
        </div>

        <!-- Bottom Row: Redis and Azure Storage -->
        <div class="card">
          <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Redis Status</h3>
          <div class="mt-2 text-3xl font-bold" :class="stats?.redisConnected ? 'status-active' : 'status-revoked'">
            {{ stats?.redisConnected ? 'Connected' : 'Disconnected' }}
          </div>
          <div v-if="stats?.redisConnected" class="mt-1 text-sm text-gray-600">
            Ping: {{ stats.redisPingMs.toFixed(1) }}ms
          </div>
        </div>

        <div class="card">
          <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide">Azure Storage</h3>
          <div class="mt-2 text-3xl font-bold" :class="stats?.storageConnected ? 'status-active' : 'status-revoked'">
            {{ stats?.storageConnected ? 'Connected' : 'Disconnected' }}
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-white shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button @click="clearRedisCache" :disabled="loading" class="btn btn-secondary">
            Clear Redis Cache
          </button>
          <button @click="cleanupOrphanedKeys" :disabled="loading" class="btn btn-secondary">
            Cleanup Orphaned Keys
          </button>
        </div>
      </div>

      <div v-if="stats" class="text-center text-sm text-gray-500">
        Last updated: {{ formatDate(stats.lastUpdated) }}
      </div>

      <div class="text-center">
        <button @click="refreshStats" class="btn btn-primary" :disabled="loading">
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { adminApi } from '@/services/api'
import type { DashboardStats } from '@/types'

const stats = ref<DashboardStats | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const loadStats = async () => {
  loading.value = true
  error.value = null

  try {
    // Load main dashboard stats
    const dashboardStats = await adminApi.getDashboardStats()

    // Load additional stats in parallel
    const [storageStats] = await Promise.allSettled([
      adminApi.getStorageStats()
    ])

    // Combine all stats
    stats.value = {
      ...dashboardStats,
      storageConnected: storageStats.status === 'fulfilled' ? storageStats.value.storageConnected : false
    }
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to load dashboard statistics'
  } finally {
    loading.value = false
  }
}

const refreshStats = () => {
  loadStats()
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}



const clearRedisCache = async () => {
  if (confirm('Are you sure you want to clear the Redis cache? This will remove all cached data.')) {
    loading.value = true
    try {
      const result = await adminApi.clearRedisCache()
      alert(`Successfully cleared ${result.keysCleared} keys from Redis cache`)
      await loadStats()
    } catch (err: any) {
      error.value = err.response?.data?.error || err.message || 'Failed to clear Redis cache'
    } finally {
      loading.value = false
    }
  }
}

const cleanupOrphanedKeys = async () => {
  if (confirm('Are you sure you want to cleanup orphaned rate limit keys? This will remove rate limit keys that no longer have corresponding API keys.')) {
    loading.value = true
    try {
      const result = await adminApi.cleanupOrphanedRateLimitKeys()
      if (result.keysDeleted > 0) {
        alert(`Successfully cleaned up ${result.keysDeleted} orphaned rate limit keys`)
      } else {
        alert('No orphaned rate limit keys found')
      }
      await loadStats()
    } catch (err: any) {
      error.value = err.response?.data?.error || err.message || 'Failed to cleanup orphaned keys'
    } finally {
      loading.value = false
    }
  }
}



onMounted(() => {
  loadStats()
})
</script>


